import { observer } from "mobx-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Button, Select } from "../../../-common";
import PeopleStore from "../../../../stores/PeopleStore";
import "./PaymentInfoSection.scss";

export const NEW_CARD_OPTION_LABEL = "+ Add a New Card";

const PaymentInfoSection = props => {
  const {
    selectedCard,
    updateSelectedCard,
    enteringNewCard,
    setEnteringNewCard,
    actionButton,
    checkoutError,
    hasProductsToShip,
    onKeapPaymentSubmit,
    selectedParentId,
    selectedParentInfusionsoftId
  } = props || {};

  const keapContainerRef = useRef(null);

  // Define states for button loading and payment success
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [paymentMethodCreated, setPaymentMethodCreated] = useState(false);
  const [keapSessionKeyLoaded, setKeapSessionKeyLoaded] = useState(false);

  // Function to submit the Keap form
  const submitKeapForm = useCallback(() => {
    console.log("submitKeapForm called");
    const keapPaymentMethod = document.querySelector("#keap-payment-method");
    console.log("Keap element found:", keapPaymentMethod);

    if (keapPaymentMethod) {
      console.log("Keap element type:", keapPaymentMethod.tagName);
      console.log("Keap element has submit method:", typeof keapPaymentMethod.submit);

      setIsSubmitting(true);

      try {
        keapPaymentMethod.submit();
        console.log("Keap submit() called successfully");
      } catch (error) {
        console.error("Error calling Keap submit():", error);
        setIsSubmitting(false);
        return;
      }

      // Add event listener for the Keap response
      console.log("Adding window message listener");
      if (typeof onKeapPaymentSubmit === "function") {
        window.addEventListener(
          "message",
          event => {
            console.log("Window message received:", event.data);
            // Check for success property as per Keap documentation
            if (event.data && event.data.success) {
              console.log("Payment method created successfully:", event.data.creditCardId);
              setPaymentMethodCreated(true);
              setIsSubmitting(false);

              // Refetch cards to get the newly added card
              PeopleStore.fetchCardsForParent(selectedParentId, selectedParentInfusionsoftId).then(() => {
                // After cards are fetched, pass the credit card ID to the parent
                onKeapPaymentSubmit(event.data.creditCardId);

                // Show success message briefly before closing
                setTimeout(() => {
                  setEnteringNewCard(false);
                }, 1500);
              });
            } else if (event.data && !event.data.success) {
              // Handle error case
              console.error("Keap payment error:", event.data);
              setIsSubmitting(false);
            }
          },
          { once: true }
        );
      }
    } else {
      console.error("Keap element not found in DOM");
    }
  }, [onKeapPaymentSubmit, setEnteringNewCard, selectedParentId, selectedParentInfusionsoftId]);

  // Fetch session key when entering new card mode
  useEffect(() => {
    let isMounted = true;

    if (enteringNewCard && selectedParentId && selectedParentInfusionsoftId) {
      // Reset payment method created state
      setPaymentMethodCreated(false);
      setKeapSessionKeyLoaded(false);

      const currentSessionKey = PeopleStore.keapSessionKeysByParentId?.[selectedParentId];

      // Fetch session key if not already present
      if (!currentSessionKey) {
        console.log("Fetching Keap session key for parent...");
        PeopleStore.fetchKeapSessionKeyForParent(selectedParentId, selectedParentInfusionsoftId)
          .then(sessionKey => {
            if (isMounted && sessionKey) {
              console.log("Keap session key loaded:", sessionKey);
              setKeapSessionKeyLoaded(true);
            }
          })
          .catch(error => {
            console.error("Error fetching Keap session key:", error);
          });
      } else {
        setKeapSessionKeyLoaded(true);
      }
    }

    return () => {
      isMounted = false;
    };
  }, [enteringNewCard, selectedParentId, selectedParentInfusionsoftId]);

  // Handle keap element creation and cleanup
  useEffect(() => {
    const currentSessionKey = PeopleStore.keapSessionKeysByParentId?.[selectedParentId];

    if (!enteringNewCard || !keapSessionKeyLoaded || !currentSessionKey) {
      return;
    }

    // We'll use this to track if the component is still mounted
    let isMounted = true;

    // Capture the current ref value to use in cleanup
    const currentContainer = keapContainerRef.current;

    // Delay until next frame to ensure DOM is ready
    const timeoutId = requestAnimationFrame(() => {
      if (!isMounted || !currentContainer) return;

      try {
        // Clear any existing content
        while (currentContainer.firstChild) {
          currentContainer.removeChild(currentContainer.firstChild);
        }

        // Create the keap element
        const keapElement = document.createElement("keap-payment-method");
        keapElement.setAttribute("data-key", currentSessionKey);
        keapElement.setAttribute("id", "keap-payment-method");
        currentContainer.appendChild(keapElement);

        console.log("Keap element created and attached");
      } catch (error) {
        console.error("Error creating Keap element:", error);
      }
    });

    // Clean up function
    return () => {
      isMounted = false;
      cancelAnimationFrame(timeoutId);

      // Clean up the DOM using the captured reference
      if (currentContainer) {
        try {
          while (currentContainer.firstChild) {
            currentContainer.removeChild(currentContainer.firstChild);
          }
        } catch (e) {
          console.error("Error cleaning up:", e);
        }
      }

      // Remove any global event listeners
      const keapElement = document.getElementById("keap-payment-method");
      if (keapElement && keapElement.parentNode) {
        try {
          keapElement.parentNode.removeChild(keapElement);
        } catch (e) {
          console.error("Error removing keap element:", e);
        }
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enteringNewCard, keapSessionKeyLoaded, selectedParentId]);

  const { rawCardsForParents, cardsForParents } = PeopleStore || {};
  const rawCards = rawCardsForParents?.[selectedParentId];
  const cards = cardsForParents?.[selectedParentId];

  useEffect(() => {
    if (selectedParentId && selectedParentInfusionsoftId && !rawCards) {
      PeopleStore?.fetchCardsForParent(selectedParentId, selectedParentInfusionsoftId);
    }
  }, [selectedParentId, selectedParentInfusionsoftId, rawCards]);

  const unsetEnteringNewCard = () => setEnteringNewCard(false);

  const cardOptions = cards?.map(c => c?.label)?.concat(NEW_CARD_OPTION_LABEL);

  const currentSessionKey = PeopleStore.keapSessionKeysByParentId?.[selectedParentId];

  // Instead of rendering the keap component through JSX, we'll use a ref
  // and create the element directly in useEffect
  const cardInputs = enteringNewCard ? (
    <div className="keap-payment-wrapper" ref={keapContainerRef} style={{ minHeight: "150px" }}>
      {!currentSessionKey && <div className="loading-payment-component">Loading payment form...</div>}
      {paymentMethodCreated && (
        <div className="payment-method-success">
          <div className="success-icon">✓</div>
          <div className="success-message">Payment method saved successfully!</div>
        </div>
      )}
    </div>
  ) : (
    <div className="loading-payment-component">Select &quot;Add a New Card&quot; to enter payment details</div>
  );

  let paymentInfoContent = enteringNewCard ? (
    <div className="new-card-info-wrapper">
      <div className="input-row">
        <div className="input-wrapper">
          <div className="input-title">Card Info</div>
          {cardInputs}
        </div>
      </div>
      <div className="keap-payment-actions">
        <Button
          type="cta"
          className="keap-submit-button"
          action={() => {
            console.log("Button clicked!");
            submitKeapForm();
          }}
          disabled={isSubmitting || paymentMethodCreated || !currentSessionKey}
        >
          {isSubmitting ? "Processing..." : currentSessionKey ? "Save Payment Method" : "Loading..."}
        </Button>
        <div className="use-existing-card">
          <span onClick={unsetEnteringNewCard}>...or use a saved card</span>
        </div>
      </div>
    </div>
  ) : (
    <div className="payment-method-select-wrapper">
      <Select
        placeholder="Select Payment Method..."
        options={cardOptions}
        value={selectedCard?.label}
        onChange={updateSelectedCard}
      />
      <p className="payment-note">Our system only accepts Visa or Mastercard.</p>
    </div>
  );

  if (!hasProductsToShip) paymentInfoContent = <div className="no-purchase-necessary">(not necessary)</div>;

  const error = checkoutError ? (
    <div className={`checkout-error${enteringNewCard ? " new-card" : ""}`}>{checkoutError}</div>
  ) : null;

  return (
    <div className="payment-information">
      {paymentInfoContent}
      {error}
      {enteringNewCard ? null : actionButton}
    </div>
  );
};

export default observer(PaymentInfoSection);
